import type {
  Requirement,
  RequirementMetadata,
  RequirementTemplateSection,
  RequirementSectionKind,
} from '@ispot/shared'
import type { NotesMode } from './types'
import { SectionFrame } from './SectionFrame'
import { ProseSection } from './ProseSection'
import { UserStorySection } from './UserStorySection'
import { CriteriaSection } from './CriteriaSection'
import { LinkedPersonaSection } from './LinkedPersonaSection'

interface Props {
  section: RequirementTemplateSection
  requirement: Requirement
  mode: NotesMode
  onUpdateRequirement: (patch: Partial<Requirement>) => void
  onUpdateMetadata: (patch: Partial<RequirementMetadata>) => void
}

/**
 * Routes a template section to the right typed editor based on its `kind`.
 * The "shape" of section content is the editor's concern; this just hands
 * each editor its current value + a typed setter.
 */
export function SectionDispatcher({
  section, requirement, mode, onUpdateRequirement, onUpdateMetadata,
}: Props) {
  const isMissing = !isPopulated(requirement, section.kind, section.id)
  const meta = requirement.metadata ?? {}

  const frameProps = { section, isMissing, mode }

  switch (section.kind) {
    case 'description':
      return (
        <SectionFrame {...frameProps}>
          <ProseSection
            section={section}
            value={requirement.description ?? ''}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            placeholder="What does this requirement do?"
            onUpdateMetadata={onUpdateMetadata}
            onChange={(next) => onUpdateRequirement({ description: next || null })}
          />
        </SectionFrame>
      )
    case 'why':
      return (
        <SectionFrame {...frameProps}>
          <ProseSection
            section={section}
            value={typeof meta.why === 'string' ? meta.why : ''}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            placeholder="Why now?"
            onUpdateMetadata={onUpdateMetadata}
            onChange={(next) => onUpdateMetadata({ why: next || undefined })}
          />
        </SectionFrame>
      )
    case 'what':
      return (
        <SectionFrame {...frameProps}>
          <ProseSection
            section={section}
            value={typeof meta.what === 'string' ? meta.what : ''}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            placeholder="What does it actually do?"
            onUpdateMetadata={onUpdateMetadata}
            onChange={(next) => onUpdateMetadata({ what: next || undefined })}
          />
        </SectionFrame>
      )
    case 'open_questions':
      return (
        <SectionFrame {...frameProps}>
          <ProseSection
            section={section}
            value={typeof meta.openQuestions === 'string' ? meta.openQuestions : ''}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            placeholder="What's still uncertain?"
            onUpdateMetadata={onUpdateMetadata}
            onChange={(next) => onUpdateMetadata({ openQuestions: next || undefined })}
          />
        </SectionFrame>
      )
    case 'user_story':
      return (
        <SectionFrame {...frameProps}>
          <UserStorySection
            section={section}
            value={meta.userStory}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            onUpdateMetadata={onUpdateMetadata}
            onChange={(next) => onUpdateMetadata({ userStory: next })}
          />
        </SectionFrame>
      )
    case 'acceptance_criteria':
      return (
        <SectionFrame {...frameProps}>
          <CriteriaSection
            section={section}
            value={meta.acceptanceCriteria}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            onUpdateMetadata={onUpdateMetadata}
            onChange={(next) => onUpdateMetadata({ acceptanceCriteria: next })}
          />
        </SectionFrame>
      )
    case 'linked_persona':
      return (
        <SectionFrame {...frameProps}>
          <LinkedPersonaSection
            section={section}
            value={undefined}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            onUpdateMetadata={onUpdateMetadata}
            onChange={() => {}}
          />
        </SectionFrame>
      )
    case 'custom': {
      const customs = (meta.customSections ?? {}) as Record<string, string>
      return (
        <SectionFrame {...frameProps}>
          <ProseSection
            section={section}
            value={customs[section.id] ?? ''}
            isMissing={isMissing}
            mode={mode}
            requirement={requirement}
            placeholder="Add notes…"
            onUpdateMetadata={onUpdateMetadata}
            onChange={(next) => {
              const updated = { ...customs, [section.id]: next }
              if (!next) delete updated[section.id]
              onUpdateMetadata({ customSections: updated })
            }}
          />
        </SectionFrame>
      )
    }
  }
}

/** Mirror of the API's isSectionPopulated — kept locally so the UI can color "missing" cues without round-tripping. */
function isPopulated(r: Requirement, kind: RequirementSectionKind, sectionId: string): boolean {
  const meta = (r.metadata ?? {}) as Record<string, unknown>
  const isNonEmpty = (v: unknown): boolean => typeof v === 'string' && v.trim().length > 0
  switch (kind) {
    case 'description':         return isNonEmpty(r.description)
    case 'why':                 return isNonEmpty(meta.why)
    case 'what':                return isNonEmpty(meta.what)
    case 'open_questions':      return isNonEmpty(meta.openQuestions)
    case 'user_story': {
      const us = meta.userStory as RequirementMetadata['userStory']
      if (!us) return false
      if (typeof us === 'string') return us.trim().length > 0
      return Boolean(us.role?.trim() && us.want?.trim())
    }
    case 'acceptance_criteria': {
      const list = meta.acceptanceCriteria as unknown[] | undefined
      return Array.isArray(list) && list.length > 0
    }
    case 'linked_persona':
      return (r.personas?.length ?? 0) > 0
        || Boolean((meta.linkedPersona as { name?: string } | undefined)?.name?.trim())
    case 'custom': {
      const customs = meta.customSections as Record<string, string> | undefined
      return isNonEmpty(customs?.[sectionId])
    }
  }
}
